/**
 * The site-visit negotiation's state machine, as the PA-facing view needs to know it.
 *
 * This module is a **direct port of connect-service's `SiteVisitTransitionEnum`** — for each
 * transition, the states it may be taken from, the state it lands in, and the party permitted to take
 * it. It is deliberately a data table rather than a set of per-state button lists, for the same reason
 * the Java side is: the rules can be read, and tested exhaustively, without reading the code that
 * applies them. When the Java enum changes, this table changes with it and `site-visit-state.util.spec.ts`
 * is what catches the drift.
 *
 * The view gates every affordance on {@link canAssociate}, so a transition that is illegal for the
 * current state is simply **absent** rather than offered and rejected. The server still validates —
 * this is the second lock, not the only one.
 *
 * <h2>One negotiation over one appointment, and it is a symmetric loop</h2>
 *
 * An appointment is a calendar day **and** an exact arrival time, proposed together and agreed
 * together. `PROPOSE_VISIT` puts the first one on the table; from there exactly one proposal is ever
 * outstanding and exactly one party owes a reply, so every subsequent move is one of two answers to it:
 *
 * ```
 * NEEDS_AVAILABILITY
 *      | PROPOSE_VISIT (associate: day + time + duration + arrival plan)
 *      v
 * PENDING_SITE_CONTACT_APPROVAL --- CONTACT_ACCEPT ------> TIME_CONFIRMED
 *      ^                     |                                  |
 *      | PA_COUNTER          | CONTACT_COUNTER                  | COMPLETE
 *      |                     v                                  v
 *      +---------------- PENDING_PA_APPROVAL          SITE_VISIT_COMPLETED
 *                            | PA_ACCEPT
 *                            +-------------------------> TIME_CONFIRMED
 * ```
 *
 * `PENDING_SITE_CONTACT_APPROVAL` and `PENDING_PA_APPROVAL` are the same state seen from either end:
 * one party's proposal is outstanding and the other owes a reply. Which of the two the document sits
 * in is what says whose move it is.
 *
 * **Why the accept/counter pairs are named per side rather than shared.** A single `ACCEPT` would have
 * to list *both* pending states in its `legalSources`, and the actor rule is what would then have to
 * keep the two apart. It could not: the rule resolves against the negotiation, not against the state,
 * so the same `ACCEPT` would be legal for the associate at `PENDING_SITE_CONTACT_APPROVAL` — the
 * associate accepting **their own** outstanding proposal, an agreement one party reached alone. Four
 * named edges make the actor/source pairing a property of the table.
 */

/**
 * The twelve statuses, verbatim from `SiteVisitStatusEnum`.
 *
 * 🔴 **Five of them are RETIRED and unreachable, and they must not be removed from this union.**
 * `PENDING_PA_SELECTION`, `APPROVED`, `PENDING_TIME_PROPOSAL`, `PENDING_TIME_CONFIRMATION` and
 * `REJECTED_ALTERNATIVES_PROVIDED`
 * belonged to the two-phase flow this replaced, in which the parties first agreed a day and an AM/PM
 * window and only then negotiated a clock time. **Nothing transitions into any of them** — see
 * {@link SITE_VISIT_TRANSITIONS}, which is the only definition of reachable behavior. They are kept
 * because the server keeps them: `status` is a typed enum on the document mapped through
 * `Enum.valueOf`, which throws on a name it does not know, so an orphaned row still arrives here and
 * still has to render. Their exits stay in the table too — `RESCHEDULE`'s and `CANCEL`'s legal
 * *sources* are what stop such a row being stranded with no move available to it at all.
 */
export type SiteVisitStatus =
  | 'NEEDS_AVAILABILITY'
  | 'AWAITING_SITE_CONTACT_INFO'
  | 'PENDING_SITE_CONTACT_APPROVAL'
  | 'PENDING_PA_APPROVAL'
  /**
   * 🔴 Retired — nothing transitions into it, and it is the retired name most likely to be sitting on
   * disk: it is the state `PENDING_PA_APPROVAL` replaced, and under the old table every
   * reject-with-alternatives came to rest here. Kept so an orphaned row still renders — and kept under
   * *this* name, because the name is what is stored.
   */
  | 'PENDING_PA_SELECTION'
  /** 🔴 Retired — nothing transitions into it. Kept so an orphaned row still renders. */
  | 'APPROVED'
  /** 🔴 Retired — nothing transitions into it. Kept so an orphaned row still renders. */
  | 'PENDING_TIME_PROPOSAL'
  /** 🔴 Retired — nothing transitions into it. Kept so an orphaned row still renders. */
  | 'PENDING_TIME_CONFIRMATION'
  | 'TIME_CONFIRMED'
  /** 🔴 Retired — nothing transitions into it. Kept so an orphaned row still renders. */
  | 'REJECTED_ALTERNATIVES_PROVIDED'
  | 'SITE_VISIT_COMPLETED'
  | 'CANCELLED';

/** Every status, in the enum's declaration order — the iteration set the spec's totality checks use. */
export const SITE_VISIT_STATUSES: SiteVisitStatus[] = [
  'NEEDS_AVAILABILITY',
  'AWAITING_SITE_CONTACT_INFO',
  'PENDING_SITE_CONTACT_APPROVAL',
  'PENDING_PA_APPROVAL',
  'PENDING_PA_SELECTION',
  'APPROVED',
  'PENDING_TIME_PROPOSAL',
  'PENDING_TIME_CONFIRMATION',
  'TIME_CONFIRMED',
  'REJECTED_ALTERNATIVES_PROVIDED',
  'SITE_VISIT_COMPLETED',
  'CANCELLED',
];

/** The transitions, verbatim from `SiteVisitTransitionEnum`. */
export type SiteVisitTransition =
  | 'SITE_CONTACT_PROVIDED'
  | 'PROPOSE_VISIT'
  | 'CONTACT_ACCEPT'
  | 'CONTACT_COUNTER'
  | 'PA_ACCEPT'
  | 'PA_COUNTER'
  | 'RESCHEDULE'
  | 'COMPLETE'
  | 'CANCEL';

/** The parties, verbatim from `SiteVisitActorEnum`. */
export type SiteVisitActor =
  | 'PROFESSIONAL_ASSOCIATE'
  | 'SITE_CONTACT'
  | 'PROJECT_MANAGER'
  | 'SYSTEM';

/**
 * `SiteVisitTransitionEnum.ActorRule` — one of these resolves against the negotiation rather than
 * being a fixed set, which is where `routedToPm` does its work.
 *
 * The old `TIME_CONFIRMER` rule — "whoever did not put the current time on the table" — is gone with
 * the two-phase flow, on both sides. Its job is now done by the table itself: `CONTACT_ACCEPT` and
 * `PA_ACCEPT` are separate edges with fixed actors and one legal source each, so the party is a static
 * fact about the transition rather than something computed per document.
 */
type ActorRule = 'SYSTEM_ONLY' | 'ASSOCIATE_ONLY' | 'ASSOCIATE_OR_SYSTEM' | 'SITE_CONTACT_SIDE';

interface TransitionRule {
  /** The one state this transition lands in. */
  target: SiteVisitStatus;
  /** The states it may be taken from. Anything else is out of order. */
  legalSources: SiteVisitStatus[];
  actorRule: ActorRule;
}

/**
 * The shape of the negotiation the resolving actor rule needs. `routedToPm` defaults to `false`, which
 * is the shape of a negotiation involving a real site contact.
 */
export interface SiteVisitActorContext {
  /** The client opted out of involving the site contact, so those steps belong to the project manager. */
  routedToPm?: boolean;
  /*
   * 🔴 `timeSuggestedByContact` was here and is gone. It existed for `ActorRule.TIME_CONFIRMER`
   * ("whoever did not put the current time on the table may confirm it"), which the four named
   * accept/counter edges replaced: the actor is now pinned by the transition's own name and its single
   * legal source, so nothing is resolved against the negotiation any more. It survived Task 7 as a
   * member no rule read, on the strength of two callers still passing it; Task 10 dropped it from the
   * projection those callers build from, leaving a member with no reader AND no writer. A rule that
   * needed "who proposed the outstanding appointment" should take `proposedBy` — the actor
   * connect-service now stores — rather than resurrect a boolean that could only ever answer half of
   * it.
   */
}

/** The transition table. One entry per `SiteVisitTransitionEnum` constant, same order. */
export const SITE_VISIT_TRANSITIONS: Record<SiteVisitTransition, TransitionRule> = {
  /**
   * The site contact snapshot arrived, so the associate can be asked to propose. order-service owns the
   * contact, which is why this is a system step rather than something a party submits — and since the
   * two `OPEN_*` steps went with the two-phase flow, it is now the only `SYSTEM_ONLY` transition.
   */
  SITE_CONTACT_PROVIDED: {
    target: 'NEEDS_AVAILABILITY',
    legalSources: ['AWAITING_SITE_CONTACT_INFO'],
    actorRule: 'SYSTEM_ONLY',
  },
  /**
   * The associate proposes the appointment: a day, an exact arrival time, a duration and an arrival
   * plan, in one move. Named for what it does — it was `SUBMIT_AVAILABILITY` while the first round
   * agreed only a day and an AM/PM window.
   */
  PROPOSE_VISIT: {
    target: 'PENDING_SITE_CONTACT_APPROVAL',
    legalSources: ['NEEDS_AVAILABILITY'],
    actorRule: 'ASSOCIATE_ONLY',
  },
  /**
   * The site contact accepts the appointment on the table, agreeing the day and the time together. The
   * agreed values are the proposed ones — the server copies them from the stored proposal rather than
   * taking them from the answer, so nothing on this side sends a time with it.
   */
  CONTACT_ACCEPT: {
    target: 'TIME_CONFIRMED',
    legalSources: ['PENDING_SITE_CONTACT_APPROVAL'],
    actorRule: 'SITE_CONTACT_SIDE',
  },
  /**
   * The site contact counters with their own day and time. Nothing is agreed — the proposal is now
   * theirs and the associate owes the reply, which is what `PENDING_PA_APPROVAL` says.
   */
  CONTACT_COUNTER: {
    target: 'PENDING_PA_APPROVAL',
    legalSources: ['PENDING_SITE_CONTACT_APPROVAL'],
    actorRule: 'SITE_CONTACT_SIDE',
  },
  /** The associate accepts the site contact's counter. The mirror of `CONTACT_ACCEPT`. */
  PA_ACCEPT: {
    target: 'TIME_CONFIRMED',
    legalSources: ['PENDING_PA_APPROVAL'],
    actorRule: 'ASSOCIATE_ONLY',
  },
  /**
   * The associate counters the site contact's counter, putting a fresh day and time back out to them.
   * The mirror of `CONTACT_COUNTER`, and the edge that makes the loop a loop — it is uncapped, so
   * propose → counter → counter → counter → accept is a legal history.
   */
  PA_COUNTER: {
    target: 'PENDING_SITE_CONTACT_APPROVAL',
    legalSources: ['PENDING_PA_APPROVAL'],
    actorRule: 'ASSOCIATE_ONLY',
  },
  /**
   * The associate moves an **already-agreed** appointment. The one edge that walks the negotiation
   * backwards: it names a replacement day and time and hands it back to the site contact for approval.
   *
   * Distinct from `PA_COUNTER` rather than a widening of it, exactly as the enum has it: the two land
   * in the same state but say different things about how the negotiation got there — "they countered
   * and we countered back" versus "we had an appointment and gave it up". Server-side it is also the
   * one transition that CLEARS fields (`confirmedDate`, `confirmedTime`, `confirmedAt`), which is why
   * the write-safety layer had to grow a clear-set for it; nothing on this side needs to know that
   * beyond expecting those fields to come back null.
   *
   * Its legal sources deliberately include the three retired day-agreed states. In a document created
   * by this machine only `TIME_CONFIRMED` can be the source; the other three are there because
   * orphaned documents sit in them, and rescheduling is the move that gets one back into the live loop
   * rather than cancelling it outright.
   *
   * `ASSOCIATE_ONLY`, and the site contact deliberately cannot take it — they act through a public
   * tokenized link, and a state-changing move there that discards an agreed appointment needs its own
   * security review. Contact-initiated rescheduling is deferred, not inherited.
   */
  RESCHEDULE: {
    target: 'PENDING_SITE_CONTACT_APPROVAL',
    legalSources: [
      'APPROVED',
      'PENDING_TIME_PROPOSAL',
      'PENDING_TIME_CONFIRMATION',
      'TIME_CONFIRMED',
    ],
    actorRule: 'ASSOCIATE_ONLY',
  },
  /**
   * The visit happened.
   *
   * 🔴 Its legal sources **narrowed from four to one**. Under the two-phase flow the exact time was
   * optional, so a visit could go ahead on an agreed window with the clock-time round still open, and
   * completion had to be legal from all three of those states as well. An exact time is now required,
   * so `TIME_CONFIRMED` is the only state in which there is an appointment to have happened. A
   * deliberate consequence: an associate can no longer mark a visit complete while a counter is
   * outstanding, which under the old table they effectively could.
   */
  COMPLETE: {
    target: 'SITE_VISIT_COMPLETED',
    legalSources: ['TIME_CONFIRMED'],
    actorRule: 'ASSOCIATE_OR_SYSTEM',
  },
  /**
   * The negotiation is abandoned. Legal from every non-terminal state — the five retired ones
   * included, so an orphaned document is not stranded.
   */
  CANCEL: {
    target: 'CANCELLED',
    legalSources: [
      'NEEDS_AVAILABILITY',
      'AWAITING_SITE_CONTACT_INFO',
      'PENDING_SITE_CONTACT_APPROVAL',
      'PENDING_PA_APPROVAL',
      'PENDING_PA_SELECTION',
      'REJECTED_ALTERNATIVES_PROVIDED',
      'APPROVED',
      'PENDING_TIME_PROPOSAL',
      'PENDING_TIME_CONFIRMATION',
      'TIME_CONFIRMED',
    ],
    actorRule: 'ASSOCIATE_OR_SYSTEM',
  },
};

/** Every transition, in declaration order. */
export const SITE_VISIT_TRANSITION_NAMES = Object.keys(
  SITE_VISIT_TRANSITIONS
) as SiteVisitTransition[];

/** A state no transition leads out of — `SiteVisitTransitionEnum.isTerminal`. */
export function isTerminalStatus(status: SiteVisitStatus | null | undefined): boolean {
  return status === 'SITE_VISIT_COMPLETED' || status === 'CANCELLED';
}

/** `ActorRule.siteContactSide` — routing REPLACES the party rather than adding one. */
function siteContactSide(routedToPm: boolean): SiteVisitActor[] {
  return routedToPm ? ['PROJECT_MANAGER'] : ['SITE_CONTACT'];
}

/** `SiteVisitTransitionEnum.permittedActors`. */
export function permittedActors(
  transition: SiteVisitTransition,
  context: SiteVisitActorContext = {}
): SiteVisitActor[] {
  const routedToPm = !!context.routedToPm;
  switch (SITE_VISIT_TRANSITIONS[transition].actorRule) {
    case 'SYSTEM_ONLY':
      return ['SYSTEM'];
    case 'ASSOCIATE_ONLY':
      return ['PROFESSIONAL_ASSOCIATE'];
    case 'ASSOCIATE_OR_SYSTEM':
      return ['PROFESSIONAL_ASSOCIATE', 'SYSTEM'];
    case 'SITE_CONTACT_SIDE':
      return siteContactSide(routedToPm);
  }
}

/** `SiteVisitTransitionEnum.isLegalFrom`. */
export function isLegalFrom(
  transition: SiteVisitTransition,
  status: SiteVisitStatus | null | undefined
): boolean {
  if (!status) {
    return false;
  }
  return SITE_VISIT_TRANSITIONS[transition].legalSources.includes(status);
}

/**
 * The one predicate the view gates on: may the professional associate take this transition on a
 * negotiation in this state and this shape? Both halves must hold — legal source *and* permitted actor.
 */
export function canAssociate(
  transition: SiteVisitTransition,
  status: SiteVisitStatus | null | undefined,
  context: SiteVisitActorContext = {}
): boolean {
  return (
    isLegalFrom(transition, status) &&
    permittedActors(transition, context).includes('PROFESSIONAL_ASSOCIATE')
  );
}

/**
 * The mirror of {@link canAssociate} for the other end of the loop: may the site-contact side — the
 * site contact, or the project manager standing in for them when the client opted out — take this
 * transition here?
 *
 * Routing replaces the party rather than adding one, so this asks about whichever of the two the
 * negotiation's `routedToPm` names, and a site contact cannot act on a negotiation routed to the PM.
 */
export function canSiteContactSide(
  transition: SiteVisitTransition,
  status: SiteVisitStatus | null | undefined,
  context: SiteVisitActorContext = {}
): boolean {
  const actors = permittedActors(transition, context);
  return (
    isLegalFrom(transition, status) &&
    (actors.includes('SITE_CONTACT') || actors.includes('PROJECT_MANAGER'))
  );
}

/** Every transition the associate may take from this state — the affordance set, derived not listed. */
export function associateTransitionsFor(
  status: SiteVisitStatus | null | undefined,
  context: SiteVisitActorContext = {}
): SiteVisitTransition[] {
  return SITE_VISIT_TRANSITION_NAMES.filter((transition) =>
    canAssociate(transition, status, context)
  );
}

/**
 * The subset of {@link associateTransitionsFor} the negotiation is actually **blocked on** — every
 * transition only the associate may take, from a state where it is legal.
 *
 * 🔴 **This exists because the affordance set is the wrong question for a queue.**
 * `associateTransitionsFor` answers "what may the PA do here", and its answer includes `COMPLETE` and
 * `CANCEL`, which are `ASSOCIATE_OR_SYSTEM` and legal from almost every state. A queue that grouped on
 * that would put nearly every visit under *Awaiting you* and stop meaning anything — the whole value of
 * the page is that the first group is short and true. So the queue asks this instead: a non-empty
 * answer means the flow cannot move until the PA moves it.
 *
 * The two live beside each other on purpose. They are derived from the same table by the same
 * predicate, so a transition added to `SiteVisitTransitionEnum` lands in both without either being
 * edited, and neither can drift from the other.
 *
 * `context` is accepted for signature symmetry with {@link associateTransitionsFor} and is passed
 * through to {@link canAssociate}; it cannot change this result, because `ASSOCIATE_ONLY` resolves to
 * the associate unconditionally and nothing else in the table does.
 */
export function pendingAssociateTransitionsFor(
  status: SiteVisitStatus | null | undefined,
  context: SiteVisitActorContext = {}
): SiteVisitTransition[] {
  return SITE_VISIT_TRANSITION_NAMES.filter(
    (transition) =>
      canAssociate(transition, status, context) &&
      SITE_VISIT_TRANSITIONS[transition].actorRule === 'ASSOCIATE_ONLY' &&
      !NON_BLOCKING_ASSOCIATE_TRANSITIONS.includes(transition)
  );
}

/**
 * `ASSOCIATE_ONLY` moves that the negotiation is nevertheless **not blocked on** — available to the PA,
 * but not something anyone is waiting for them to do.
 *
 * 🔴 **Do not delete this and let the `ASSOCIATE_ONLY` filter stand alone.** `RESCHEDULE` is legal from
 * `TIME_CONFIRMED` and from the three retired day-agreed states, and `TIME_CONFIRMED` is where a
 * healthy negotiation spends the rest of its life. Without this exclusion every agreed visit would land
 * in the queue's *Awaiting you* group on the strength of an escape hatch nobody asked the PA to take,
 * which is precisely the failure {@link pendingAssociateTransitionsFor} exists to avoid: the group's
 * whole value is that it is short and true. A confirmed appointment is not waiting on the PA merely
 * because the PA *could* move it. (`TIME_CONFIRMED` is in fact grouped by name rather than by
 * emptiness — but the three retired states are decided by this set, and an orphaned row sitting in one
 * of them should not read as work owed either.)
 *
 * The affordance set ({@link associateTransitionsFor}) still carries it, which is what the row's
 * Reschedule control and the negotiation panel's reschedule form are gated on — so the move is offered
 * everywhere it is legal while the grouping keeps meaning what it says. Same table, one question each.
 */
export const NON_BLOCKING_ASSOCIATE_TRANSITIONS: SiteVisitTransition[] = ['RESCHEDULE'];

/**
 * A human label for every state — the PA sees where the negotiation stands even when there is nothing
 * for them to do. The five retired states keep labels for the same reason they keep their places in the
 * union: an orphaned row still renders. Sentence case: these are labels, not buttons.
 */
const STATUS_LABEL: Record<SiteVisitStatus, string> = {
  NEEDS_AVAILABILITY: 'Propose a date and time',
  AWAITING_SITE_CONTACT_INFO: 'Awaiting site contact info',
  PENDING_SITE_CONTACT_APPROVAL: 'Awaiting site contact approval',
  PENDING_PA_APPROVAL: 'Your response needed',
  // 🔴 Retired, and deliberately NOT phrased as work owed. Under the old table the PA really did owe a
  // pick from the alternatives the contact had offered; those alternatives are gone from the model, and
  // no move but CANCEL is legal here, so a label that asked for a selection would ask for something
  // this state can no longer accept.
  PENDING_PA_SELECTION: 'Alternatives offered — no longer on record',
  APPROVED: 'Approved — visit scheduled',
  PENDING_TIME_PROPOSAL: 'Propose your exact arrival time',
  PENDING_TIME_CONFIRMATION: 'Awaiting time confirmation',
  TIME_CONFIRMED: 'Time confirmed — visit locked',
  REJECTED_ALTERNATIVES_PROVIDED: 'Rescheduled — alternatives provided',
  SITE_VISIT_COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

/**
 * The label overrides for a negotiation whose site-contact steps were routed to the project manager.
 * Routing replaces the party, so a label that names the site contact would name the wrong person.
 */
const ROUTED_TO_PM_LABEL: Partial<Record<SiteVisitStatus, string>> = {
  PENDING_SITE_CONTACT_APPROVAL: 'Awaiting project manager approval',
};

/** The state's label. An unrecognized or absent status degrades to a neutral string, never throws. */
export function statusLabel(
  status: SiteVisitStatus | null | undefined,
  context: SiteVisitActorContext = {}
): string {
  if (!status) {
    return 'Site visit';
  }
  if (context.routedToPm && ROUTED_TO_PM_LABEL[status]) {
    return ROUTED_TO_PM_LABEL[status] as string;
  }
  return STATUS_LABEL[status] ?? String(status);
}

/** The `.pill--*` tone for the status chip. Absent/unknown falls back to the neutral info tone. */
export function statusPillClass(status: SiteVisitStatus | null | undefined): string {
  switch (status) {
    case 'APPROVED':
    case 'TIME_CONFIRMED':
    case 'SITE_VISIT_COMPLETED':
      return 'pill--success';
    case 'NEEDS_AVAILABILITY':
    case 'PENDING_PA_APPROVAL':
    case 'REJECTED_ALTERNATIVES_PROVIDED':
    case 'PENDING_TIME_PROPOSAL':
      return 'pill--warning';
    case 'CANCELLED':
      return 'pill--danger';
    default:
      return 'pill--info';
  }
}
